import { object, string, mixed } from "yup";
import { REQUIRED_FIELD } from "@shared-kernel/primary/forms/constants";
import {
  CONTRACT_SIGNATORY_TYPE,
  isDelegateSignatoryType,
  type ContractSignatory,
} from "@academy-context/shared/domain/types/contract-signatory";
import type { Nullable } from "@shared-kernel/core/types/nullable";

export interface ContractSignatoryFormInputs {
  // Null until the user explicitly picks one: the sign button only shows up after that choice
  type: Nullable<CONTRACT_SIGNATORY_TYPE>;
  firstName: string;
  lastName: string;
  email: string;
}

export const contractSignatoryFormSchema = object().shape({
  type: mixed<CONTRACT_SIGNATORY_TYPE>().oneOf(Object.values(CONTRACT_SIGNATORY_TYPE)).nullable().required(REQUIRED_FIELD),
  firstName: string().when("type", { is: isDelegateSignatoryType, then: schema => schema.trim().required(REQUIRED_FIELD) }),
  lastName: string().when("type", { is: isDelegateSignatoryType, then: schema => schema.trim().required(REQUIRED_FIELD) }),
  email: string().when("type", {
    is: isDelegateSignatoryType,
    then: schema => schema.trim().required(REQUIRED_FIELD).email("Email invalide"),
  }),
});

// A previous choice is shown again, so that the same choice reuses the pending contract
export const formatSignatoryToFormData = (signatory: Nullable<ContractSignatory>): ContractSignatoryFormInputs => {
  if (!signatory) {
    return { type: null, firstName: "", lastName: "", email: "" };
  } else if (signatory.type === CONTRACT_SIGNATORY_TYPE.LEGAL_REPRESENTATIVE) {
    return { type: signatory.type, firstName: "", lastName: "", email: "" };
  } else if (signatory.type === CONTRACT_SIGNATORY_TYPE.DELEGATE) {
    return { type: signatory.type, firstName: signatory.firstName, lastName: signatory.lastName, email: signatory.email };
  } else {
    const unhandledSignatory: never = signatory;
    throw new Error(`Unknown contract signatory type. Signatory: ${JSON.stringify(unhandledSignatory)}.`);
  }
};

export const formatFormToSignatory = (inputs: ContractSignatoryFormInputs): ContractSignatory => {
  const { type } = inputs;
  if (type === null) {
    throw new Error("No contract signatory chosen.");
  } else if (type === CONTRACT_SIGNATORY_TYPE.LEGAL_REPRESENTATIVE) {
    return { type };
  } else if (type === CONTRACT_SIGNATORY_TYPE.DELEGATE) {
    return { type, firstName: inputs.firstName.trim(), lastName: inputs.lastName.trim(), email: inputs.email.trim().toLowerCase() };
  } else {
    const unhandledType: never = type;
    throw new Error(`Unknown contract signatory type. Type: ${unhandledType}.`);
  }
};
